import { SpreadsheetColumnSettingsDef } from "@components/spreadsheet/SpreadsheetColumnSettings";

/**
 * localStorage key used to persist the user's hidden-column preferences for
 * the native invoice line-items spreadsheet. Intentionally not workspace-
 * scoped — column visibility is a UI preference that should survive workspace
 * switches.
 */
export const INVOICE_LINE_ITEMS_SPREADSHEET_COLUMNS_STORAGE_KEY =
    "native-invoice-line-items-hidden-columns";

/**
 * Columns hidden on first render / after Reset. Class and receivables account
 * are secondary coding fields that most invoices don't need on every line.
 */
export const INVOICE_LINE_ITEMS_DEFAULT_HIDDEN_FIELDS: readonly string[] = [
    "classIds",
    "receivablesAccountId",
];

interface InvoiceLineColumnMeta extends SpreadsheetColumnSettingsDef {
    /** CSS track width used when building `gridTemplateColumns`. */
    gridTrack: string;
}

export interface InvoiceLineColumnViewAvailability {
    showClassColumn: boolean;
    showReceivablesAccountColumn: boolean;
    isTaxPerLineItem: boolean;
    isDiscountPerLineItem: boolean;
    isDepositPerLineItem: boolean;
}

/**
 * Whether a line-item column should appear in the Items table Views picker
 * (and therefore be toggleable / reorderable there). Tax / Discount / Deposit
 * stay out of Views unless their per-line-item editing flag is on — matching
 * Class / receivables, which are omitted when their feature gate is off.
 * Income account is always listed.
 */
export function isInvoiceLineColumnAvailableInViews(
    field: string,
    availability: InvoiceLineColumnViewAvailability,
): boolean {
    if (field === "classIds") {
        return availability.showClassColumn;
    }
    if (field === "receivablesAccountId") {
        return availability.showReceivablesAccountColumn;
    }
    if (field === "tax") {
        return availability.isTaxPerLineItem;
    }
    if (field === "discount") {
        return availability.isDiscountPerLineItem;
    }
    if (field === "deposit") {
        return availability.isDepositPerLineItem;
    }
    return true;
}

/**
 * Canonical ordered list of invoice line-item spreadsheet columns.
 *
 * - `immutable` columns are always visible and cannot be hidden.
 * - Tax / Discount / Deposit columns are gated by per-line-item editing
 *   (`isTaxPerLineItem` / …) at the call site — omitted from Views until
 *   that flag is on.
 * - `classIds` is gated by the CLASSES plan feature at the call site.
 * - Income account is always available. Receivables is gated by
 *   ACCRUAL_LEDGER + a primary accrual ledger on the selected entity.
 */
export const INVOICE_LINE_COLUMNS: readonly InvoiceLineColumnMeta[] = [
    {
        field: "productOrService",
        label: "Item",
        immutable: true,
        gridTrack: "minmax(150px, 1fr)",
    },
    {
        field: "description",
        label: "Description",
        immutable: true,
        gridTrack: "minmax(150px, 1fr)",
    },
    {
        field: "quantity",
        label: "Quantity",
        immutable: true,
        gridTrack: "80px",
    },
    {
        field: "unitAmount",
        label: "Rate",
        immutable: true,
        gridTrack: "104px",
    },
    {
        field: "amount",
        label: "Amount",
        immutable: true,
        gridTrack: "112px",
    },
    {
        field: "tax",
        label: "Tax",
        immutable: true,
        gridTrack: "88px",
    },
    {
        field: "discount",
        label: "Discount",
        immutable: true,
        gridTrack: "88px",
    },
    {
        field: "deposit",
        label: "Deposit",
        immutable: true,
        gridTrack: "88px",
    },
    {
        field: "classIds",
        label: "Class",
        immutable: false,
        gridTrack: "120px",
    },
    {
        field: "accrualAccountId",
        label: "Income account",
        immutable: false,
        gridTrack: "160px",
    },
    {
        field: "receivablesAccountId",
        label: "Receivables account",
        immutable: false,
        gridTrack: "160px",
    },
];

export const LINE_ACTIONS_COLUMN_WIDTH_PX = 40;

/**
 * Builds a CSS `grid-template-columns` string from the visible column metas,
 * appending the trailing actions track.
 */
export function buildInvoiceLineGridTemplateColumns(
    columns: readonly InvoiceLineColumnMeta[],
): string {
    return [
        ...columns.map((column) => column.gridTrack),
        `${LINE_ACTIONS_COLUMN_WIDTH_PX}px`,
    ].join(" ");
}
